import { useApolloClient, useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { errorCode } from '@/account/arcade-api';
import { usePerson } from '@/account/usePerson';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { DownloadMenu } from '@/components/DownloadMenu/DownloadMenu';
import { EntityDetail } from '@/components/EntityDetail/EntityDetail';
import { EntityTypeBadge } from '@/components/EntityTypeBadge/EntityTypeBadge';
import { ExtractionGraph } from '@/components/ExtractionGraph/ExtractionGraph';
import { ExtractionStats } from '@/components/ExtractionStats/ExtractionStats';
import { JobProgress } from '@/components/JobProgress/JobProgress';
import { JobStatusBadge } from '@/components/JobStatusBadge/JobStatusBadge';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RunDetails } from '@/components/RunDetails/RunDetails';
import { Spinner } from '@/components/Spinner/Spinner';
import { TrashButton } from '@/components/TrashButton/TrashButton';
import { formatRelative } from '@/lib/format';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { isAudioJob, isInFlight, isStreaming, JOB, jobTitle } from '@/papermill/api';
import {
  categoryOf,
  entityLabel,
  fieldRows,
  humanize,
  relationshipsOf,
  toGraph,
  type Extraction,
} from '@/papermill/extraction';
import { withDemoKey } from '@/papermill/demo-key';
import { loadExtraction } from '@/papermill/extraction-cache';
import { endLiveSession } from '@/papermill/live';
import { warmPages } from '@/papermill/pdf-pages';
import { jobState, stageIndex, stagesOf } from '@/papermill/stages';
import { formatDuration } from '@/papermill/transcript';

import styles from './JobContainer.module.css';
import { SourceViewer } from './SourceViewer';
import { TranscriptJobContainer } from './TranscriptJobContainer';
import { useDeleteJob } from './useDeleteJob';

const POLL_MS = 2000;

type Loaded =
  { id: string; url: string; extraction: Extraction } | { id: string; url: string; failed: true };

/** One job: live progress while it runs, its graph (and transcript) once done, the log either way. */
export function JobContainer() {
  const { id = '' } = useParams();
  const { data, error, refetch, startPolling, stopPolling } = useQuery(JOB, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });
  const job = data?.job;
  const running = job ? isInFlight(job) : false;
  const extractionUrl = job?.result?.downloads.extraction ?? null;
  const pdfUrl = job?.result?.downloads.canonicalPdf ?? null;
  const transcriptUrl = job?.result?.downloads.transcript ?? null;
  const audioUrl = job?.result?.downloads.audio ?? null;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [endingLive, setEndingLive] = useState(false);
  const phone = useMediaQuery('(max-width: 640px)');
  const client = useApolloClient();
  const { person } = usePerson();
  const navigate = useNavigate();
  const deletion = useDeleteJob(() => navigate('/playground', { replace: true }));

  useEffect(() => {
    if (running) {
      startPolling(POLL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [running, startPolling, stopPolling]);

  useEffect(() => {
    if (!extractionUrl) {
      return;
    }
    let cancelled = false;
    loadExtraction(id, extractionUrl)
      .then((extraction) => !cancelled && setLoaded({ id, url: extractionUrl, extraction }))
      .catch(() => !cancelled && setLoaded({ id, url: extractionUrl, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [id, extractionUrl]);

  const extraction =
    loaded && loaded.id === id && 'extraction' in loaded ? loaded.extraction : null;
  const extractionFailed = Boolean(loaded && loaded.url === extractionUrl && 'failed' in loaded);
  const graph = useMemo(() => (extraction ? toGraph(extraction) : null), [extraction]);

  useEffect(() => {
    if (!pdfUrl || !extraction) {
      return;
    }
    const located = new Set<number>();
    for (const entity of extraction.entities ?? []) {
      for (const field of Object.values(entity.fields ?? {})) {
        if (field.bbox) located.add(field.bbox.page);
        for (const occurrence of field.occurrences ?? []) {
          if (occurrence.bbox) located.add(occurrence.bbox.page);
        }
      }
    }
    void warmPages(
      pdfUrl,
      [...located].sort((a, b) => a - b),
    );
  }, [pdfUrl, extraction]);
  const selected = extraction?.entities?.find((entity) => entity.id === selectedId) ?? null;
  const back = <Link to="/playground">← All jobs</Link>;

  if (!job) {
    if (error) {
      return (
        <>
          <PageHeader back={back} title="Job" />
          <Callout
            tone="danger"
            title={
              errorCode(error) === 'JOB_NOT_FOUND'
                ? 'This job does not exist'
                : 'Could not load this job'
            }
          >
            {errorCode(error) === 'JOB_NOT_FOUND' ? null : (
              <Button variant="ghost" onClick={() => void refetch()}>
                Try again
              </Button>
            )}
          </Callout>
        </>
      );
    }
    return <Spinner />;
  }

  const state = jobState(job);
  const audio = isAudioJob(job);
  const noun = audio ? 'recording' : 'document';
  const metrics = job.result?.metrics ?? null;
  const seconds =
    typeof metrics?.total_seconds === 'number'
      ? (metrics.total_seconds as number)
      : job.submittedAt && job.completedAt
        ? (Date.parse(job.completedAt) - Date.parse(job.submittedAt)) / 1000
        : null;
  const fieldCount = (extraction?.entities ?? [])
    .filter((entity) => !entity.discarded)
    .reduce((sum, entity) => sum + Object.keys(entity.fields ?? {}).length, 0);
  const labelFor = (entityId: string) => {
    const entity = extraction?.entities?.find((candidate) => candidate.id === entityId);
    return entity ? entityLabel(entity) : entityId;
  };

  const downloads = [
    pdfUrl ? { label: 'Cleaned PDF', href: pdfUrl } : null,
    audioUrl ? { label: 'Recording', href: audioUrl } : null,
    transcriptUrl ? { label: 'Transcript', href: transcriptUrl } : null,
    extractionUrl ? { label: audio ? 'Extraction' : 'Extraction JSON', href: extractionUrl } : null,
  ].filter((download) => download !== null);

  return (
    <div>
      <PageHeader
        back={back}
        corner={
          <div className={styles.corner}>
            <DownloadMenu items={downloads} />
            <TrashButton
              label={`Delete ${jobTitle(job)}`}
              onClick={() => deletion.requestDelete(job)}
            />
          </div>
        }
        title={
          <>
            {jobTitle(job)}{' '}
            {running ? null : <JobStatusBadge label={state.label} tone={state.tone} />}
          </>
        }
        lede={
          phone
            ? undefined
            : [
                job.submittedAt ? `Submitted ${formatRelative(job.submittedAt)}` : null,
                audio
                  ? job.durationMs
                    ? formatDuration(job.durationMs)
                    : null
                  : job.pageCount
                    ? `${job.pageCount} ${job.pageCount === 1 ? 'page' : 'pages'}`
                    : null,
                job.credits !== null
                  ? `${job.credits} ${job.credits === 1 ? 'credit' : 'credits'}`
                  : null,
                job.isSample ? 'Sample' : null,
              ]
                .filter(Boolean)
                .join(' · ')
        }
      />

      <div className={styles.body}>
        {isStreaming(job) ? (
          <Callout title="This session is still live">
            It is being recorded somewhere else, or its tab was closed without finishing. End it to
            transcribe what was recorded so far.{' '}
            <Button
              variant="ghost"
              loading={endingLive}
              onClick={async () => {
                if (!person) {
                  return;
                }
                setEndingLive(true);
                try {
                  await withDemoKey(client, person.id, (secret) => endLiveSession(job.id, secret));
                  await refetch();
                } catch {
                  // Already ended or not yet started; the refetch below shows which.
                  void refetch();
                } finally {
                  setEndingLive(false);
                }
              }}
            >
              End session
            </Button>
          </Callout>
        ) : null}

        {running ? (
          <JobProgress
            current={stageIndex(job)}
            stages={stagesOf(job)}
            onDelete={() => deletion.requestDelete(job)}
            {...(audio
              ? {
                  title:
                    job.kind === 'live'
                      ? 'Turning your session into a transcript'
                      : 'Listening to your recording',
                  queuedTitle: 'Waiting to start…',
                  lede: 'Updates by itself · a long call takes a few minutes',
                }
              : {})}
          />
        ) : null}

        {state.tone === 'danger' ? (
          <Callout tone="danger" title="This job failed">
            {state.detail ?? `The platform could not finish this ${noun}.`} The log below has the
            details. No credits were charged.
          </Callout>
        ) : null}

        {state.tone === 'warning' ? (
          <Callout title={`This ${noun} was refused`}>
            {state.detail}. Nothing was extracted and no credits were charged.
          </Callout>
        ) : null}

        {audio && state.tone === 'success' ? (
          <TranscriptJobContainer
            jobId={job.id}
            transcriptUrl={transcriptUrl}
            audioUrl={audioUrl}
            extraction={extraction}
            extractionFailed={extractionFailed}
            durationMs={job.durationMs ?? null}
            tookSeconds={seconds}
            onRetry={() => void refetch()}
          />
        ) : null}

        {!audio && state.tone === 'success' && !graph && !extractionFailed ? <Spinner /> : null}
        {!audio && extractionFailed ? (
          <Callout tone="danger" title="Could not load the extraction">
            <Button variant="ghost" onClick={() => void refetch()}>
              Try again
            </Button>
          </Callout>
        ) : null}

        {!audio && graph ? (
          <section aria-label="Extraction">
            <ExtractionStats
              stats={[
                { label: 'Entities', value: graph.nodes.length },
                { label: 'Relationships', short: 'Links', value: graph.links.length },
                { label: 'Values read', short: 'Values', value: fieldCount },
                { label: 'Pages', value: job.pageCount ?? extraction?.page_count ?? '—' },
                { label: 'Took', value: seconds !== null ? `${Math.round(seconds)}s` : '—' },
              ]}
            />
            {graph.nodes.length > 0 ? (
              <ExtractionGraph nodes={graph.nodes} links={graph.links} onSelect={setSelectedId} />
            ) : (
              <Callout title="Nothing to draw">
                Papermill read the document but found no entities in it.
              </Callout>
            )}
          </section>
        ) : null}

        <RunDetails
          job={job}
          metrics={metrics}
          manifest={job.result?.manifest ?? null}
          open={state.tone === 'danger'}
        />
      </div>

      {deletion.dialog}

      {!audio && selected && extraction ? (
        <Dialog
          title={entityLabel(selected)}
          eyebrow={
            <EntityTypeBadge
              typeLabel={humanize(selected.type)}
              category={categoryOf(selected.type)}
            />
          }
          onClose={() => setSelectedId(null)}
          size="full"
        >
          <EntityDetail
            key={selected.id}
            fields={fieldRows(selected.fields)}
            relationships={relationshipsOf(selected.id, extraction.edges ?? []).map(
              (relationship) => ({
                label: relationship.label,
                direction: relationship.direction,
                otherId: relationship.otherId,
                otherLabel: labelFor(relationship.otherId),
              }),
            )}
            onOpenEntity={setSelectedId}
            renderSource={({ bbox, label }) =>
              pdfUrl && bbox ? (
                <SourceViewer
                  url={pdfUrl}
                  bbox={bbox}
                  label={label}
                  onRetry={() => void refetch()}
                />
              ) : (
                <p>The cleaned PDF is not available for this job.</p>
              )
            }
          />
        </Dialog>
      ) : null}
    </div>
  );
}
